// When a store owner writes back, the rep pastes what they said and gets
// two short replies in their own voice, built from the fixed scripts and
// pricing. The rep edits and sends from their own phone or inbox; nothing
// goes out from here. This is the one writing use with controlled
// evidence behind it, and it helps the newer rep most.
import React, { useState } from 'react';
import { Check, Copy, MessageSquare, RefreshCw } from 'lucide-react';
import { assist, prospectForAssist } from './salesAssist';
import { currentAction, fillScript, smsLink, stageOf } from './salesPlan';
import { e164 } from './salesData';
import { Field, btnGhost, btnPrimary, inputCls } from './salesUi';

const CHANNELS = [
  { id: 'text', label: 'Text' },
  { id: 'dm', label: 'Facebook or Instagram' },
  { id: 'email', label: 'Email' },
];

export default function ReplyDrafter({ client, storeId, prospect: p, me, onClose, onUseAsNote, initialText = '' }) {
  const [channel, setChannel] = useState(initialText ? 'email' : p.email && !p.phone ? 'email' : 'text');
  const [theySaid, setTheySaid] = useState(initialText);
  const [busy, setBusy] = useState(false);
  const [options, setOptions] = useState([]);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  const tel = e164(p.phone);

  const draft = async () => {
    if (!theySaid.trim() || busy) return;
    setBusy(true);
    setError('');
    try {
      const a = currentAction(p);
      const res = await assist(client, storeId, 'reply', {
        they_said: theySaid.trim(),
        channel,
        rep_name: me?.name || '',
        prospect: prospectForAssist(p),
        stage: `${stageOf(p.stage, p).label}${a ? `, next step: ${a.label}` : ''}`,
        last_script: a ? fillScript(a.say, { prospect: p, rep: me }) : '',
        brief_facts: p.brief?.facts ? Object.fromEntries(Object.entries(p.brief.facts).filter(([, v]) => v?.value).map(([k, v]) => [k, v.value])) : {},
      });
      setOptions(res.options || []);
      setNote(res.note || '');
    } catch (e) {
      console.error('sales: reply failed', e);
      setError(e.message || 'Could not draft a reply');
    } finally {
      setBusy(false);
    }
  };

  const copy = async (key, text) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(''), 1500); } catch { /* ignore */ }
  };

  return (
    <div className="rounded-2xl border border-violet-200 bg-violet-50/40 p-4">
      <div className="flex items-center gap-2">
        <MessageSquare className="w-4 h-4 text-violet-700" />
        <span className="text-sm font-bold text-slate-900">They wrote back. Draft a reply</span>
        <button type="button" onClick={onClose} className="ml-auto text-xs font-semibold text-slate-500 hover:text-slate-800">Close</button>
      </div>
      <div className="mt-3 flex bg-white border border-slate-200 rounded-lg p-1 w-fit">
        {CHANNELS.map((c) => (
          <button key={c.id} type="button" onClick={() => setChannel(c.id)} className={`px-2.5 py-1 rounded-md text-xs font-semibold ${channel === c.id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'}`}>{c.label}</button>
        ))}
      </div>
      <Field label="What they wrote" className="mt-3">
        <textarea value={theySaid} onChange={(e) => setTheySaid(e.target.value)} rows={3} className={inputCls} placeholder="Paste their message exactly" autoFocus />
      </Field>
      <div className="mt-2 flex items-center gap-2">
        <button type="button" onClick={draft} disabled={!theySaid.trim() || busy} className={btnPrimary}>{busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />} Two replies</button>
        {theySaid.trim() && onUseAsNote && <button type="button" onClick={() => onUseAsNote(theySaid.trim())} className={btnGhost} title="Put their words in the note so it is logged with the outcome">Use as the note</button>}
        {error && <span className="text-sm text-red-600">{error}</span>}
      </div>
      {options.length > 0 && (
        <div className="mt-3 space-y-2">
          {note && <p className="text-xs text-slate-600">{note}</p>}
          {options.map((o, i) => (
            <div key={i} className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-violet-700">{o.label}</span>
                <span className="ml-auto flex items-center gap-1.5">
                  {channel === 'text' && tel && <a href={smsLink(tel, o.text)} className="inline-flex items-center gap-1 h-7 px-2 rounded-md bg-slate-900 text-white text-[11px] font-semibold">Open in Messages</a>}
                  {channel === 'email' && p.email && <a href={`mailto:${p.email}?subject=${encodeURIComponent((o.text.match(/^Subject:\s*(.+)$/m) || [])[1] || 'Re: Exitium')}&body=${encodeURIComponent(o.text.replace(/^Subject:.*\n+/m, ''))}`} className="inline-flex items-center gap-1 h-7 px-2 rounded-md bg-slate-900 text-white text-[11px] font-semibold">Open in Mail</a>}
                  <button type="button" onClick={() => copy(`r${i}`, o.text)} className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-slate-200 text-[11px] font-semibold text-slate-700 hover:bg-slate-50">{copied === `r${i}` ? <><Check className="w-3 h-3 text-emerald-600" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}</button>
                </span>
              </div>
              <pre className="mt-1.5 whitespace-pre-wrap font-sans text-sm text-slate-900">{o.text}</pre>
            </div>
          ))}
          <p className="text-[11px] text-slate-400">Edit before you send. It goes out from your phone or inbox, under your name.</p>
        </div>
      )}
    </div>
  );
}
